import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

function formatDate(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'yyyy-MM-dd HH:mm'); }
  catch { return iso; }
}

function pct(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(0)}%`;
}

function score(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toFixed(2);
}

/**
 * Preset column definitions. Pages select a preset via the `kind` prop
 * ('use_case' | 'issue') or override with an explicit `columns` array.
 *
 * Each column: { key, header, align?, render(cluster) }.
 * Render returns JSX; truncate/format inside.
 */
/**
 * Convert a 0..1 satisfaction score to a CSS color that smoothly transitions
 * red → amber → green. 0% = brightest red, 100% = brightest green. We pick
 * three reference HSL stops and lerp through them so the gradient stays
 * perceptually balanced (pure RGB lerp would muddy the middle).
 */
function satisfactionColor(score01) {
  const v = Math.max(0, Math.min(1, Number(score01) || 0));
  // Stops at 0.0 (red), 0.5 (amber), 1.0 (green).
  const stops = [
    { t: 0,   h: 0,   s: 78, l: 50 },   // red
    { t: 0.5, h: 42,  s: 90, l: 50 },   // amber
    { t: 1,   h: 138, s: 62, l: 42 },   // green
  ];
  let a = stops[0];
  let b = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (v >= stops[i].t && v <= stops[i + 1].t) {
      a = stops[i]; b = stops[i + 1]; break;
    }
  }
  const span = (b.t - a.t) || 1;
  const k = (v - a.t) / span;
  const h = a.h + (b.h - a.h) * k;
  const s = a.s + (b.s - a.s) * k;
  const l = a.l + (b.l - a.l) * k;
  return `hsl(${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%)`;
}

function SatisfactionBadge({ value }) {
  if (value == null) return <span className="text-[color:var(--text-muted)]">—</span>;
  const v = Math.max(0, Math.min(1, Number(value)));
  if (!Number.isFinite(v)) return <span className="text-[color:var(--text-muted)]">—</span>;
  const color = satisfactionColor(v);
  return (
    <span className="font-semibold tabular-nums" style={{ color }}>
      {`${Math.round(v * 100)}%`}
    </span>
  );
}

/** "X ago" — short relative time, capped to years. */
function formatAgo(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const s = Math.floor(diffMs / 1000);
  if (s < 60)        return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)        return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)        return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)         return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5)         return `${w}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12)       return `${mo}mo ago`;
  const y = Math.floor(d / 365);
  return `${y}y ago`;
}

export const USE_CASE_COLUMNS = [
  {
    key: 'label',
    header: 'Capability',
    render: (c) => (
      <div className="font-medium text-[color:var(--text-primary)]">
        {c.label || c.cluster_label || '(unlabeled)'}
      </div>
    ),
  },
  {
    key: 'agent',
    header: 'Agent',
    render: (c) => <span className="text-[color:var(--text-secondary)]">{c.agent_name || '—'}</span>,
  },
  {
    key: 'runs',
    header: 'Runs',
    render: (c) => (
      <span className="tabular-nums text-[color:var(--text-primary)]">
        {c.run_count ?? c.size ?? c.conversation_count ?? c.attributes?.conversation_count ?? '—'}
      </span>
    ),
  },
  {
    key: 'error',
    header: 'Error rate',
    render: (c) => <span className="tabular-nums text-[color:var(--text-primary)]">{pct(c.error_rate)}</span>,
  },
  {
    key: 'satisfaction',
    header: 'Satisfaction',
    render: (c) => <SatisfactionBadge value={c.satisfaction_score} />,
  },
  {
    key: 'last_updated',
    header: 'Last updated',
    render: (c) => (
      <span
        className="tabular-nums text-[color:var(--text-secondary)]"
        title={c.last_seen_at || c.updated_at || ''}
      >
        {formatAgo(c.last_seen_at || c.updated_at)}
      </span>
    ),
  },
];

/** Severity % for Issues list — 0..10 score shown as 0..100%, colored by severity band. */
function IssueSeverityScore({ value }) {
  const raw = Number(value);
  const sev10 = Number.isFinite(raw) ? Math.max(0, Math.min(10, raw)) : null;
  if (sev10 === null) return <span className="text-[color:var(--text-muted)]">—</span>;
  const pct = Math.round(sev10 * 10);
  const color =
    pct >= 70 ? 'var(--feedback-error)'
    : pct >= 40 ? 'var(--feedback-warning)'
    : 'var(--feedback-success)';
  return (
    <span className="tabular-nums text-[12px] font-semibold" style={{ color }}>
      {pct}%
    </span>
  );
}

/**
 * Columns for /api/issues v2 on IssuesPage — same table chrome as use cases
 * (rounded card, header row, row hover).
 */
// Category metadata duplicated here (we don't want this file to depend
// on IssuePanelV3) — keep small + the tone classes match. Renders the
// category + pattern as a compact "topic" subtitle line under the title
// so the list view matches the use-cases page's pattern of titled rows
// with topic chips.
const _ISSUE_CAT_META = {
  tool_failure:           { label: 'Tool failure',           color: 'var(--feedback-error)',   bg: 'var(--feedback-error-bg)' },
  tool_misuse:            { label: 'Tool misuse',            color: 'var(--feedback-warning)', bg: 'var(--feedback-warning-bg)' },
  generation_quality:     { label: 'Generation quality',     color: 'var(--feedback-info)',    bg: 'var(--feedback-info-bg)' },
  ux_rage:                { label: 'UX rage',                color: 'var(--brand-primary)',    bg: 'var(--brand-soft)' },
  conversation_breakdown: { label: 'Conversation breakdown', color: 'var(--feedback-success)', bg: 'var(--feedback-success-bg)' },
};

/**
 * Columns for /api/issues v2 on IssuesPage.
 * render(issue, idx) — idx is the global 0-based row index across all groups.
 */
export const ISSUES_LIST_COLUMNS = [
  {
    key: 'issue',
    header: 'Issue',
    render: (issue) => (
      <div className="flex min-w-0 flex-col gap-0.5 py-0.5">
        <span className="min-w-0 max-w-[min(100%,480px)] text-[12px] font-medium leading-snug text-[color:var(--text-primary)] line-clamp-2">
          {issue.title || '(untitled)'}
        </span>
        {(issue.is_regression || (issue.pattern && issue.pattern !== 'legacy')) ? (
          <div className="flex flex-wrap items-center gap-1">
            {issue.is_regression ? (
              <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[color:var(--feedback-error-bg)] text-[color:var(--feedback-error)]">
                Regression
              </span>
            ) : null}
            {issue.pattern && issue.pattern !== 'legacy' ? (
              <code className="rounded bg-surface-hover px-1.5 py-0.5 font-mono text-[10px] font-medium text-[color:var(--text-secondary)] ring-1 ring-[color:var(--border-default)]">
                {issue.pattern}
              </code>
            ) : null}
          </div>
        ) : null}
      </div>
    ),
  },
  {
    key: 'users',
    header: 'Users',
    align: 'right',
    render: (issue) => (
      <span className="tabular-nums text-[12px] text-[color:var(--text-primary)]">
        {(issue.affected_users ?? 0).toLocaleString()}
      </span>
    ),
  },
  {
    key: 'occurrences',
    header: 'Occurrences',
    align: 'right',
    render: (issue) => (
      <span className="tabular-nums text-[12px] text-[color:var(--text-primary)]">
        {(issue.occurrence_count ?? 0).toLocaleString()}
      </span>
    ),
  },
  {
    key: 'severity',
    header: 'Severity',
    render: (issue) => <IssueSeverityScore value={issue.severity} />,
  },
  {
    key: 'last_seen',
    header: 'Last seen',
    render: (issue) => (
      <span
        className="tabular-nums text-[11px] text-[color:var(--text-muted)]"
        title={issue.last_seen_at || issue.updated_at || ''}
      >
        {formatAgo(issue.last_seen_at || issue.updated_at)}
      </span>
    ),
  },
];

function columnAlignClass(col) {
  if (col.align === 'right') return 'text-right';
  if (col.align === 'center') return 'text-center';
  return 'text-left';
}

/** A shared cell class with the column's alignment in place of its text-left. */
function alignedCellClass(base, col) {
  return base.replace('text-left', columnAlignClass(col));
}

// Group rows (topic / category) are rows of the table, not a second header:
// same hairline and hover, no fill.
const GROUP_ROW_CLASS = tableRowClass;

function truncate(s, n) {
  if (!s) return '—';
  const str = String(s);
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
}

/**
 * Group an array of clusters by their topic_label. Returns an ordered list:
 *   [{ topicId, topicLabel, topicDescription, agentName, clusters[] }, ...]
 * Order: topics with the highest aggregate conversation_count first; the
 * single "Ungrouped" bucket (clusters with no topic_id) lands last.
 *
 * Topics are scoped per (agent_name, topic_label) — the same topic label
 * across two different agents represents two separate groupings, so we key
 * the bucket on both.
 */
/**
 * Group issues by category (tool_failure, ux_rage, …) so the issues page
 * mirrors the topic-grouped use-cases page: a collapsible bar per
 * category with the actual issue rows nested underneath. Order: highest
 * aggregate severity-weighted occurrence count first.
 */
function groupIssuesByCategory(issues) {
  if (!Array.isArray(issues) || issues.length === 0) return [];
  const buckets = new Map();
  for (const i of issues) {
    const cat = i.category || i.issue_type || 'uncategorized';
    if (!buckets.has(cat)) {
      buckets.set(cat, {
        key: `cat::${cat}`,
        category: cat,
        meta: _ISSUE_CAT_META[cat] || { label: cat, color: 'var(--text-secondary)', bg: 'var(--surface-hover)' },
        issues: [],
        totalOccurrences: 0,
      });
    }
    const b = buckets.get(cat);
    b.issues.push(i);
    b.totalOccurrences += Number(i.occurrence_count) || 0;
  }
  for (const b of buckets.values()) {
    b.issues.sort((a, b2) => (Number(b2.severity) || 0) - (Number(a.severity) || 0));
  }
  const arr = Array.from(buckets.values());
  arr.sort((a, b) => b.totalOccurrences - a.totalOccurrences);
  return arr;
}

function CategoryHeaderRow({ bucket, colSpan, collapsed, onToggle }) {
  const { meta, issues } = bucket;
  return (
    <tr
      onClick={onToggle}
      className={GROUP_ROW_CLASS}
    >
      <td colSpan={colSpan} className="px-3 align-middle">
        <div className="flex items-center gap-2">
          {collapsed ? (
            <ChevronRight size={13} className="shrink-0 text-[color:var(--text-muted)]" />
          ) : (
            <ChevronDown size={13} className="shrink-0 text-[color:var(--text-muted)]" />
          )}
          <span
            className="text-[13px] font-semibold text-[color:var(--text-primary)]"
          >
            {meta.label}
          </span>
          <span className="ml-auto inline-flex h-5 items-center rounded-full bg-surface px-2 text-[11px] font-semibold tabular-nums text-[color:var(--text-muted)] ring-1 ring-[color:var(--border-default)]">
            {issues.length}
          </span>
        </div>
      </td>
    </tr>
  );
}

function groupClustersByTopic(clusters) {
  if (!Array.isArray(clusters) || clusters.length === 0) return [];
  const buckets = new Map();
  for (const c of clusters) {
    const topicLabel = c.topic_label || null;
    const agentName = c.agent_name || '';
    // Compose a stable key. Clusters without a topic share an "Ungrouped"
    // bucket per agent so the UI never collapses unrelated agents together.
    const key = topicLabel ? `${agentName}::${topicLabel}` : `${agentName}::__ungrouped__`;
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        topicId: c.topic_id || null,
        topicLabel,
        topicDescription: c.topic_description || null,
        agentName,
        clusters: [],
        totalConversations: 0,
      });
    }
    const b = buckets.get(key);
    b.clusters.push(c);
    b.totalConversations += Number(c.conversation_count) || 0;
  }
  // Sort within each bucket by size, descending — matches the flat-table
  // default ordering.
  for (const b of buckets.values()) {
    b.clusters.sort((a, b2) => (Number(b2.conversation_count) || 0) - (Number(a.conversation_count) || 0));
  }
  // Order: real topics by aggregate size desc, "Ungrouped" buckets last.
  const arr = Array.from(buckets.values());
  arr.sort((a, b) => {
    const aUng = !a.topicLabel;
    const bUng = !b.topicLabel;
    if (aUng !== bUng) return aUng ? 1 : -1;
    if (b.totalConversations !== a.totalConversations) return b.totalConversations - a.totalConversations;
    return (a.agentName || '').localeCompare(b.agentName || '');
  });
  return arr;
}

/**
 * Topic header row. Renders inside the table as a full-width bar that
 * shows the topic label, the agent it belongs to, and an aggregate
 * conversation count. Clicking toggles collapse for the group.
 */
function TopicHeaderRow({ bucket, colSpan, collapsed, onToggle }) {
  const { topicLabel, topicDescription, clusters: bucketClusters } = bucket;
  const display = topicLabel || 'Ungrouped';
  const count = bucketClusters?.length ?? 0;
  return (
    <tr
      onClick={onToggle}
      className={GROUP_ROW_CLASS}
    >
      <td colSpan={colSpan} className="px-3 align-middle">
        <div className="flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
          {collapsed ? (
            <ChevronRight size={13} className="text-[color:var(--text-muted)]" />
          ) : (
            <ChevronDown size={13} className="text-[color:var(--text-muted)]" />
          )}
          <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">{display}</span>
          {topicDescription ? (
            <span className="hidden max-w-[40ch] truncate text-[11px] text-[color:var(--text-muted)] sm:inline">
              {topicDescription}
            </span>
          ) : null}
          <span className="ml-auto inline-flex h-5 items-center rounded-full bg-surface px-2 text-[11px] font-semibold tabular-nums text-[color:var(--text-muted)] ring-1 ring-[color:var(--border-default)]">
            {count}
          </span>
        </div>
      </td>
    </tr>
  );
}

/**
 * Cluster directory table. Three call styles:
 *   <ClustersTable clusters={...} onRowClick={...} kind="use_case" />
 *   <ClustersTable clusters={...} onRowClick={...} columns={CUSTOM_COLS} />
 *   <ClustersTable clusters={...} groupBy="topic" /> — renders a
 *     two-level hierarchy: topic headers (collapsible) above their
 *     constituent cluster rows. Topic info is read from each cluster's
 *     `topic_id` / `topic_label` / `topic_description` fields (returned
 *     by /api/agent-runs/use-cases). Clusters without a topic land under
 *     an "Ungrouped" header. Single-cluster topics still get a header so
 *     the hierarchy reads uniformly.
 *
 * Default kind falls back to the legacy 5-column layout for callers that
 * predate the split (none in-tree, but leave the door open).
 */
export default function ClustersTable({
  clusters,
  onRowClick,
  columns = null,
  emptyMessage,
  /** When set with rowKey, highlights the matching row (e.g. open drawer). */
  selectedId = null,
  /** (row) => primitive id compared to selectedId. Default: cluster_id || id */
  rowKey = null,
  /** 'topic' to render the two-level grouped layout. Default: flat. */
  groupBy = null,
}) {
  const cols = columns || USE_CASE_COLUMNS;
  const getRowKey = rowKey || ((c) => c.cluster_id || c.id);

  // Compute groups once per cluster set. Cheap — pure JS sort + group.
  const groups = useMemo(() => {
    if (groupBy === 'topic') return groupClustersByTopic(clusters);
    if (groupBy === 'category') return groupIssuesByCategory(clusters);
    return null;
  }, [clusters, groupBy]);

  // Local collapse state. Keyed by bucket.key so adding/removing groups
  // doesn't reset the user's open/closed picks.
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const toggleKey = (key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (!clusters || clusters.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface px-6 text-center text-[12px] text-[color:var(--text-muted)]">
        {emptyMessage || 'No clusters yet: they appear once the clustering job runs over enough runs.'}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
          <thead>
            <tr className={tableHeaderRowClass}>
              {cols.map((col) => (
                <th
                  key={col.key}
                  className={alignedCellClass(tableHeaderCellClass, col)}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups ? (
              // Grouped view: header row (topic or category), then child rows.
              // IIFE so we can track a running global row index across all groups.
              (() => {
                let rowIdx = 0;
                return groups.flatMap((bucket) => {
                  const collapsed = collapsedKeys.has(bucket.key);
                  const HeaderComp = groupBy === 'category' ? CategoryHeaderRow : TopicHeaderRow;
                  const headerEl = (
                    <HeaderComp
                      key={`hdr-${bucket.key}`}
                      bucket={bucket}
                      colSpan={cols.length}
                      collapsed={collapsed}
                      onToggle={() => toggleKey(bucket.key)}
                    />
                  );
                  if (collapsed) return [headerEl];
                  const children = bucket.issues || bucket.clusters || [];
                  const rowEls = children.map((c) => {
                    const idx = rowIdx++;
                    const id = getRowKey(c);
                    const isSelected = selectedId != null && id === selectedId;
                    return (
                      <tr
                        key={id}
                        onClick={() => onRowClick?.(c)}
                        className={`${tableRowClass} ${
                          isSelected ? 'bg-surface-hover' : ''
                        }`}
                      >
                        {cols.map((col) => (
                          <td
                            key={col.key}
                            className={alignedCellClass(tableDataCellClass, col)}
                          >
                            {col.render(c, idx)}
                          </td>
                        ))}
                      </tr>
                    );
                  });
                  return [headerEl, ...rowEls];
                });
              })()
            ) : (
              // Flat view.
              clusters.map((c, idx) => {
                const id = getRowKey(c);
                const isSelected = selectedId != null && id === selectedId;
                return (
                  <tr
                    key={id}
                    onClick={() => onRowClick?.(c)}
                    className={`${tableRowClass} ${
                      isSelected ? 'bg-surface-hover' : ''
                    }`}
                  >
                    {cols.map((col) => (
                      <td
                        key={col.key}
                        className={alignedCellClass(tableDataCellClass, col)}
                      >
                        {col.render(c, idx)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
